import { flexRender, getCoreRowModel, useReactTable, getSortedRowModel } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react'
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';

const ConfigurationDetails = ({ data, configurations }) => {
    const [selectedConfigIndex, setSelectedConfigIndex] = useState(0);

    // Trouver la configuration initiale (la plus ancienne)
    const initialConfig = useMemo(() => {
        return configurations.reduce((oldest, current) => {
            return new Date(current.creationDate) < new Date(oldest.creationDate) ? current : oldest;
        }, configurations[0]);
    }, [configurations]);

    const selectedConfig = configurations[selectedConfigIndex];
    const isInitialConfig = selectedConfig.id === initialConfig.id;

    // Créer les colonnes en fonction de la configuration sélectionnée
    const columns = useMemo(() => {
        const baseColumns = [
            {
                id: 'name',
                accessorFn: row => row?.mixture?.name,
                header: ({ column }) => (
                    <div className="font-semibold cursor-pointer hover:text-primary flex items-center gap-1"
                        onClick={() => column.toggleSorting()}>
                        Nom
                        {column.getIsSorted() === 'asc' ? (
                            <ArrowUpIcon className="h-4 w-4" />
                        ) : column.getIsSorted() === 'desc' ? (
                            <ArrowDownIcon className="h-4 w-4" />
                        ) : null}
                    </div>
                ),
                cell: info => <span className="text-sm">{info.getValue()}</span>,
                size: 50
            },
            {
                id: 'subCategory',
                accessorFn: row => row?.mixture?.subCategory,
                header: ({ column }) => (
                    <div className="font-semibold cursor-pointer hover:text-primary flex items-center gap-1"
                        onClick={() => column.toggleSorting()}>
                        Sous-catégorie
                        {column.getIsSorted() === 'asc' ? (
                            <ArrowUpIcon className="h-4 w-4" />
                        ) : column.getIsSorted() === 'desc' ? (
                            <ArrowDownIcon className="h-4 w-4" />
                        ) : null}
                    </div>
                ),
                cell: info => <span className="text-sm">{info.getValue()}</span>,
                size: 50
            },
            {
                id: 'dilution',
                accessorFn: row => row?.dilution ? row?.dilution.label : row?.mixture.defaultDilution.label,
                header: ({ column }) => (
                    <div className="font-semibold cursor-pointer hover:text-primary flex items-center gap-1"
                        onClick={() => column.toggleSorting()}>
                        Dilution
                    </div>
                ),
                cell: info => <span className="text-sm">{info.getValue()}</span>,
                size: 50
            },
            {
                id: 'sensibility',
                accessorFn: row => row?.sensibility || '-',
                header: ({ column }) => (
                    <div className="font-semibold cursor-pointer hover:text-primary flex items-center gap-1"
                        onClick={() => column.toggleSorting()}>
                        Sensibilité
                        {column.getIsSorted() === 'asc' ? (
                            <ArrowUpIcon className="h-4 w-4" />
                        ) : column.getIsSorted() === 'desc' ? (
                            <ArrowDownIcon className="h-4 w-4" />
                        ) : null}
                    </div>
                ),
                cell: info => {
                    const value = info.getValue();
                    return value !== '-' ? (
                        <span className="badge badge-primary">{value}</span>
                    ) : (
                        <span className="text-gray-500">-</span>
                    );
                },
                size: 50
            }
        ];

        if (!isInitialConfig) {
            // Pour les autres configurations, ajouter la sensibilité initiale et la validation
            baseColumns.push(
                {
                    id: 'validation',
                    accessorFn: row => row?.sensibility || '-',
                    header: ({ column }) => (
                        <div className="font-semibold cursor-pointer hover:text-primary flex items-center gap-1"
                            onClick={() => column.toggleSorting()}>
                            Validation
                            {column.getIsSorted() === 'asc' ? (
                                <ArrowUpIcon className="h-4 w-4" />
                            ) : column.getIsSorted() === 'desc' ? (
                                <ArrowDownIcon className="h-4 w-4" />
                            ) : null}
                        </div>
                    ),
                    cell: info => {
                        const value = info.getValue();
                        return value !== '-' ? (
                            <span className="badge badge-accent">{value}</span>
                        ) : (
                            <span className="text-gray-500">-</span>
                        );
                    },
                    size: 50
                }
            );
        }
        return baseColumns;
    }, [isInitialConfig, initialConfig, selectedConfig]);

    const configuration = useMemo(() => {
        return selectedConfig?.configuration.content || [];
    }, [selectedConfig?.configuration]);

    const table = useReactTable({
        data: configuration,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        defaultColumn: {
            size: 75,
            minSize: 20,
            maxSize: Number.MAX_SAFE_INTEGER,
        },
    });

    const headers = table.getFlatHeaders();
    const rows = table.getRowModel()?.rows;

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
                <select
                    className="select select-sm select-bordered w-full max-w-xs"
                    value={selectedConfigIndex}
                    onChange={(e) => setSelectedConfigIndex(Number(e.target.value))}
                >
                    {configurations.map((config, index) => (
                        <option key={config.id} value={index}>
                            Validation {index === 0 ? 'Initiale' : index + 1} {config.isActive && '(Active)'}
                        </option>
                    ))}
                </select>
            </div>

            <div className="grid grid-cols-3 gap-4">
                <div className="stat bg-base-200 rounded-lg p-4">
                    <div className="stat-title">Total</div>
                    <div className="stat-value text-primary">{selectedConfig?.configuration.total}</div>
                </div>
                <div className="stat bg-base-200 rounded-lg p-4">
                    <div className="stat-title">Physiques</div>
                    <div className="stat-value text-secondary">{selectedConfig?.configuration.totalPhysique}</div>
                </div>
                <div className="stat bg-base-200 rounded-lg p-4">
                    <div className="stat-title">Effluences</div>
                    <div className="stat-value text-accent">{selectedConfig?.configuration.totalEffluence}</div>
                </div>
            </div>

            {data && configuration.length > 0 && (
                <div className="overflow-x-auto bg-base-100 rounded-lg shadow">
                    <table className="table table-sm w-full">
                        <thead>
                            <tr>
                                {headers.map(header => (
                                    <th
                                        key={header.id}
                                        style={{ width: header.getSize() }}
                                        className=""
                                    >
                                        <div className="flex items-center gap-2">
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <tr
                                    key={row.id}
                                    className="!bg-base-100"
                                >
                                    {row.getVisibleCells().map(cell => (
                                        <td
                                            key={cell.id}
                                            style={{ width: cell.column.getSize() }}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default ConfigurationDetails;
